import { inject, Injectable } from "@angular/core";
import { Cart, OptionData, OrderItem, OrderPublic, AddItemToCartVariables } from "../../../globalTypes/order.types";
import { Store } from "@ngrx/store";
import { getInteractionsByFulfillmentForBuyerOrSeller, GetInteractionsByFulfillmentForBuyerOrSellerVariables, getOrder, GetOrderVariables, sendMessage, SendMessageVariables } from "@dataconnect/account";
import { getFormattedOrder, sleep } from "../../../globalTypes/_helpers";
import { OrdersActions } from "../store/order.store";
import { Logger } from "../helper/logger";
import { InteractionsPageActions } from "../store/interactions.store";
import { InteractionPublic } from "../../../globalTypes/interactions.types";
import { MessageData, MessageDialogComponent } from "../pages/order/message.dialog";
import { lastValueFrom, take } from "rxjs";
import { accountFeature } from "../store/account.store";
import { MatDialog } from "@angular/material/dialog";
import { CancelOrderDialogComponent } from "../pages/order/cancel.order.dialog";
import { Fulfillment, FulfillmentStatusType } from "../../../globalTypes/fulfillment.types";
import { MatSnackBar } from "@angular/material/snack-bar";
import { LocationPublic } from "../../../globalTypes/account.types";
import { onCallFunctions } from "../../../globalTypes/backend.api.types";
import { Functions } from "@angular/fire/functions";
import { CancelOrderData, StartRefundResult } from "../../../globalTypes/refund.types";
import { httpsCallable } from "firebase/functions";
import { UpdateItemQuantityInCartOnCallVariables } from "../../../globalTypes/onCall.types";
const log = new Logger("OrderService")
@Injectable({
  providedIn: 'root'
})
export class OrderService {
  functions = inject(Functions);
  constructor(
    private store: Store,
    private dialog: MatDialog,
    private snackbar: MatSnackBar,
  ){}
  
  async hydrateCart (orderId: string, locationId: string){
    try {
      const vars: GetOrderVariables = {
        orderId,
        locationId,
      }
      await sleep(1000);
      const updatedOrderRef = await getOrder(vars);
      if(updatedOrderRef.data){
        const rawCart = updatedOrderRef.data.orders[0] as unknown as OrderPublic;
        console.log({rawCart})
        const cart = getFormattedOrder(rawCart, "location") as Cart;
        this.store.dispatch(OrdersActions.updateCart({cart}))
      }
    } catch(error) {
      log.error({hydrateError: error})
    }
  }

  async addItemToCartAndHydrate (addItemToCartVars: AddItemToCartVariables, cart: Partial<Cart>){
    const { listingVariationId, orderId, quantity, fulfilledToLocationId:locationId } = addItemToCartVars;
    // check if item already exists in the cart before adding
    const existingOrderItem = cart && cart.orderItems_on_order && cart.orderItems_on_order.find(item => item.listingVariation.id === listingVariationId);
    // if it does, just update the quantity instead of adding a new item
    if(existingOrderItem){
      const itemQuantityVars: UpdateItemQuantityInCartOnCallVariables = {
        orderItemId: existingOrderItem.id,
        orderId, quantity, locationId,
      }
      await this.updateItemQuantityAndHydrate(itemQuantityVars);
      return;
    }
    const callable = httpsCallable<AddItemToCartVariables, OrderPublic>(this.functions, onCallFunctions.addItemToCartAndHydrate);
    try {
      const result = await callable(addItemToCartVars);
      console.log({result});
      const updatedCart = getFormattedOrder(result.data, "location") as Cart;
      this.store.dispatch(OrdersActions.updateCart({cart: updatedCart}));
    } catch(error) {
      log.error({error})
      this.snackbar.open("An error occurred while adding the item to the cart. Please try again.", "close", {duration: 5000})
      return;
    }
  }

  async updateItemQuantityAndHydrate (updateItemQuantityVars: UpdateItemQuantityInCartOnCallVariables){    
    const callable = httpsCallable<UpdateItemQuantityInCartOnCallVariables, OrderPublic>(this.functions, onCallFunctions.updateItemQuantityAndHydrate);
    try {
      const result = await callable(updateItemQuantityVars);
      console.log({result});
      const updatedCart = getFormattedOrder(result.data, "location") as Cart;
      this.store.dispatch(OrdersActions.updateCart({cart: updatedCart}));
    } catch(error) {
      log.error({error})
      this.snackbar.open("An error occurred while updating the item quantity in the cart. Please try again.", "close", {duration: 5000})
      return;
    }
  }

  async hydrateBuyerInteractions (fulfillmentId: string, locationId: string){
    const vars: GetInteractionsByFulfillmentForBuyerOrSellerVariables = {
      buyerId: locationId,
      fulfillmentId,
    }
    await sleep(1000);
    this.hydrateInteractions(vars);
  }

  async hydrateSellerInteractions (fulfillmentId: string, locationId: string){
    const vars: GetInteractionsByFulfillmentForBuyerOrSellerVariables = {
      sellerId: locationId,
      fulfillmentId,
    }
    await sleep(1000);
    this.hydrateInteractions(vars);
  }

  async hydrateInteractions (vars: GetInteractionsByFulfillmentForBuyerOrSellerVariables){
    await sleep(1000);
    const interactionRef = await getInteractionsByFulfillmentForBuyerOrSeller(vars);
    const interactions = interactionRef.data.interactions as unknown as InteractionPublic[];
    this.store.dispatch(InteractionsPageActions.interactionsLoaded({interactions}))
  }

  async startRefund( optionData: OptionData, isBuyer: boolean){
    console.log({optionData})
    const fulfillment = optionData.fulfillment;
    const order = optionData.order;
    const fulfillmentId = fulfillment.id as string;
    const persistingOrderItems = order.fulfillments_on_order.reduce((acc, fulfillment) => {
        const itemsForFulfillment = order.orderItems_by_fulfillment ? order.orderItems_by_fulfillment[fulfillment.id!] : [];
        const persistingItems = itemsForFulfillment.filter(item => !item.refundId);
        return [...acc, ...persistingItems];
      }, [] as OrderItem[])
    const orderItems = persistingOrderItems.map((item: any) => ({ ...item, selected: true}));
    const parcel = fulfillment.parcels_on_fulfillment.find(parcel => parcel.id === persistingOrderItems[0].parcelId)
    var data: CancelOrderData = {
      fulfillmentId: fulfillment.id,
      fulfillmentStatus: fulfillment.statusId as FulfillmentStatusType,
    persistingOrderItems: orderItems,
      claimedToBeFaulty: undefined,
      restockItems: optionData.restockItems || false,
      selectedOrderItems: orderItems,
      parcel,
      isBuyer,
      message: "",
    }
    console.log({data})
    const dialog = this.dialog.open(CancelOrderDialogComponent, {data});
    const cancelResults: CancelOrderData = await lastValueFrom(dialog.afterClosed().pipe(take(1)));
    if(!cancelResults) return;
    console.log({cancelResults: cancelResults})
    cancelResults.selectedOrderItems = cancelResults.persistingOrderItems.filter((item: any) => item.selected)
    // update status and send email to seller with cancelResults.message as reason for cancellation
    const callable = httpsCallable<CancelOrderData, StartRefundResult>(this.functions, onCallFunctions.startRefund);
    let refundStarted = false;
    try {
      const result = await callable(cancelResults);
      console.log({result});
      refundStarted = result.data.success;
    } catch(error) {
      log.error({error})
      this.snackbar.open("An error occurred while starting the refund process. Please try again.", "close", {duration: 5000})
      return;
    }
    if(refundStarted){
      const completeCallable = httpsCallable(this.functions, onCallFunctions.completeRefund);
      try {
        await completeCallable({fulfillmentId: fulfillment.id});
        this.snackbar.open("Refund process started successfully", "close", {duration: 5000})
      } catch(error) {
        log.error({error})
        this.snackbar.open("An error occurred while completing the refund process. Please try again.", "close", {duration: 5000})
        return;
       }
    } else {
      this.snackbar.open("Failed to start refund process. Please try again.", "close", {duration: 5000})
    }    
  }
  
  async sendMessageDialog(optionData: any, isIssue: boolean){
    const fulfillment = optionData.fulfillment;
    const locations = await lastValueFrom(this.store.select<{[locationId: string]: LocationPublic}>(accountFeature.selectLocations).pipe(take(1)))
    console.log({locations})
    const senderId =  fulfillment.fulfilledByLocationId
    const receiverId = fulfillment.fulfilledToLocationId
    const receiverLocation = locations[senderId]
    const senderLocation = locations[receiverId]
    const isBuyer = senderLocation?.id === fulfillment.fulfilledToLocationId
    const data: MessageData = { isBuyer, isIssue, message: "", placeholder: "Message "+ (receiverLocation ? "to "+receiverLocation.name : "" )}
    const dialog = this.dialog.open(MessageDialogComponent, {data});
    const results: MessageData | undefined = await lastValueFrom(dialog.afterClosed().pipe(take(1)));
    if(!results) return;
    const body = results.message
    const vars: SendMessageVariables = {
      buyerInitiated: true,
      fulfillmentId: fulfillment.id,
      buyerId: fulfillment.fulfilledToLocationId,
      sellerId: fulfillment.fulfilledByLocationId,
      buyerAlert: false,
      sellerAlert: true,
      isInitial: false,
      adminAlert: !!isIssue,
      subject: isIssue ? "Issue Reported" : "Message Sent",
      body,
    }
    const sendMessageRef = await sendMessage(vars);
    console.log({sendMessageRef})
    this.snackbar.open("Sending " + (isIssue ? "Issue Report" : "Message"), undefined, {duration: 3000})
    console.log({vars, isBuyer, receiverLocation, senderLocation})
    if(isBuyer) {
      await this.hydrateBuyerInteractions(fulfillment.id, senderLocation.id);
    } else {      
      await this.hydrateSellerInteractions(fulfillment.id, receiverLocation.id);
    }
    this.snackbar.open((isIssue ? "Issue Reported" : "Message Sent"), "close", {duration: 3000})

  }

}